// MessageQueueTab — live view of the notification message queue.
//
// Summary cards (Pending / Processing / Sent / Failed / Total) auto-refresh
// every 30 s. Admin-only "Run Queue Now" button calls POST /notifications/flush
// (rate-limited 1/30s; 429 handled gracefully). Filter row: Status + Channel.
// Table columns match legacy reports.php column order.

import { useState } from 'react';
import { Mail, MessageSquare } from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import { EmptyState } from '../../../../components/ui/EmptyState/EmptyState';
import { Pagination } from '../../../../components/ui/Pagination/Pagination';
import { StatusBadge } from '../../../../components/ui/StatusBadge/StatusBadge';
import type { BadgeVariant } from '../../../../components/ui/Badge/Badge';
import { useAuthStore } from '../../../../store/authStore';
import { useNotificationQueue } from '../../hooks/useNotificationQueue';
import { useNotificationSummary } from '../../hooks/useNotificationSummary';
import { useFlushQueue } from '../../hooks/useFlushQueue';
import type { MessageQueueRow } from '../../../../types/api';
import type { QueueFilters } from '../../../../api/notifications';
import styles from './MessageQueueTab.module.css';

type QueueStatus = QueueFilters['status'];
type QueueType = QueueFilters['type'];

const QUEUE_STATUS_MAP: Record<MessageQueueRow['status'], BadgeVariant> = {
  pending: 'warning',
  processing: 'info',
  sent: 'success',
  failed: 'danger',
  cancelled: 'muted',
};

const QUEUE_STATUS_LABELS: Record<MessageQueueRow['status'], string> = {
  pending: 'Pending',
  processing: 'Processing',
  sent: 'Sent',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const DEFAULT_LIMIT = 20;

/** Tiny inline helper — no date-fns dependency required */
function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 0) return 'just now';
  const diffS = Math.floor(diffMs / 1000);
  if (diffS < 60) return `${diffS}s ago`;
  const diffM = Math.floor(diffS / 60);
  if (diffM < 60) return `${diffM}m ago`;
  const diffH = Math.floor(diffM / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return `${Math.floor(diffH / 24)}d ago`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function truncate(s: string | null, max: number): string {
  if (!s) return '—';
  return s.length > max ? s.slice(0, max) + '…' : s;
}

export function MessageQueueTab() {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const [status, setStatus] = useState<QueueStatus>('all');
  const [type, setType] = useState<QueueType>('all');
  const [page, setPage] = useState(1);

  const filters: QueueFilters = { status, type, page, limit: DEFAULT_LIMIT };

  const queueQuery = useNotificationQueue(filters);
  const summaryQuery = useNotificationSummary();
  const flushMutation = useFlushQueue();

  const rows = queueQuery.data?.rows ?? [];
  const pagination = queueQuery.data?.pagination;
  const summary = summaryQuery.data;

  function handleStatusChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setStatus(e.target.value as QueueStatus);
    setPage(1);
  }

  function handleTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setType(e.target.value as QueueType);
    setPage(1);
  }

  function handleClearFilters() {
    setStatus('all');
    setType('all');
    setPage(1);
  }

  function handleRunQueue() {
    flushMutation.mutate();
  }

  const totalPages = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? 0;

  return (
    <div className={styles.tab}>
      {/* ── Summary cards ─────────────────────────────────────────────── */}
      <div className={styles.summaryRow}>
        <div className={styles.card}>
          <span className={styles.cardLabel}>Pending</span>
          <span className={styles.cardValue}>{summary?.pending ?? '—'}</span>
          <span className={styles.cardSub}>Awaiting send</span>
        </div>
        <div className={styles.card}>
          <span className={styles.cardLabel}>Processing</span>
          <span className={styles.cardValue}>{summary?.processing ?? '—'}</span>
          <span className={styles.cardSub}>In flight</span>
        </div>
        <div className={styles.card}>
          <span className={styles.cardLabel}>Sent</span>
          <span className={styles.cardValue}>{summary?.sent ?? '—'}</span>
          <span className={styles.cardSub}>Delivered</span>
        </div>
        <div className={styles.card}>
          <span className={styles.cardLabel}>Failed</span>
          <span className={styles.cardValue}>{summary?.failed ?? '—'}</span>
          <span className={styles.cardSub}>Needs attention</span>
        </div>
        <div className={styles.card}>
          <span className={styles.cardLabel}>Total</span>
          <span className={styles.cardValue}>{summary?.total ?? '—'}</span>
          <span className={styles.cardSub}>All time</span>
        </div>
      </div>

      {/* ── Top bar: inline badges + Run Queue Now + last run ─────────── */}
      <div className={styles.topBar}>
        <div className={styles.summaryBadges}>
          <span className={`${styles.badgeChip} ${styles.badgeChipAmber}`}>
            {summary?.pending ?? 0} Pending
          </span>
          <span className={`${styles.badgeChip} ${styles.badgeChipBlue}`}>
            {summary?.processing ?? 0} Processing
          </span>
          <span className={`${styles.badgeChip} ${styles.badgeChipGreen}`}>
            {summary?.sent ?? 0} Sent
          </span>
          <span className={`${styles.badgeChip} ${styles.badgeChipRed}`}>
            {summary?.failed ?? 0} Failed
          </span>
        </div>

        <span className={styles.lastRun}>
          Last run: {formatRelativeTime(summary?.last_run_at ?? null)}
        </span>

        {isAdmin && (
          <Button
            variant="primary"
            size="sm"
            loading={flushMutation.isPending}
            disabled={flushMutation.isPending}
            onClick={handleRunQueue}
            aria-label="Run Queue Now"
          >
            Run Queue Now
          </Button>
        )}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="qStatusFilter">Status</label>
          <select
            id="qStatusFilter"
            className={styles.filterSelect}
            value={status}
            onChange={handleStatusChange}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="qChannelFilter">Channel</label>
          <select
            id="qChannelFilter"
            className={styles.filterSelect}
            value={type}
            onChange={handleTypeChange}
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            Clear filters
          </Button>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────── */}
      <div className={styles.tableWrap}>
        <div className={styles.tableHeader}>
          <span className={styles.tableTitle}>Message Queue</span>
          <span className={styles.tableMeta}>
            {total} message{total !== 1 ? 's' : ''} total
            {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ''}
          </span>
        </div>

        {rows.length === 0 ? (
          <EmptyState
            title="No notifications in queue"
            description={
              status !== 'all' || type !== 'all'
                ? 'Try clearing the filters to see more results.'
                : 'The message queue is empty.'
            }
          />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Recipient</th>
                    <th>Subject / Content</th>
                    <th>Status</th>
                    <th>Attempts</th>
                    <th>Created</th>
                    <th>Sent</th>
                    <th>Next Retry</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isFailed = row.status === 'failed';
                    const attRatio = row.max_attempts > 0 ? row.attempts / row.max_attempts : 0;
                    const attCls =
                      row.attempts >= row.max_attempts
                        ? styles.attChipDanger
                        : attRatio > 0
                        ? styles.attChipWarn
                        : '';
                    const subjectDisplay =
                      row.type === 'email' ? row.subject : row.body_preview;

                    return (
                      <tr
                        key={row.id}
                        className={isFailed ? styles.rowFailed : undefined}
                        data-failed={isFailed ? 'true' : undefined}
                      >
                        <td>
                          <span
                            className={`${styles.typeBadge} ${
                              row.type === 'email' ? styles.typeEmail : styles.typeSms
                            }`}
                          >
                            {row.type === 'email' ? (
                              <Mail size={12} aria-hidden />
                            ) : (
                              <MessageSquare size={12} aria-hidden />
                            )}
                            {row.type === 'email' ? 'Email' : 'SMS'}
                          </span>
                        </td>
                        <td>
                          <div className={styles.cellPrimary}>
                            {truncate(row.recipient, 32)}
                          </div>
                          {row.recipient_name && (
                            <div className={styles.cellSub}>{row.recipient_name}</div>
                          )}
                        </td>
                        <td>
                          <div className={styles.cellPrimary}>
                            {truncate(subjectDisplay, 55)}
                          </div>
                        </td>
                        <td>
                          <StatusBadge
                            status={row.status}
                            statusMap={QUEUE_STATUS_MAP}
                            formatLabel={(s) => QUEUE_STATUS_LABELS[s] ?? s}
                          />
                        </td>
                        <td>
                          <span className={`${styles.attChip} ${attCls}`}>
                            {row.attempts}/{row.max_attempts}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            {formatDate(row.created_at)}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            {formatDate(row.sent_at)}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            {formatDate(row.next_retry_at)}
                          </span>
                        </td>
                        <td>
                          {row.error_message ? (
                            <span
                              className={styles.errorText}
                              title={row.error_message}
                            >
                              {truncate(row.error_message, 40)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && pagination && (
              <div className={styles.paginationWrap}>
                <Pagination
                  pagination={{
                    page: pagination.page,
                    limit: pagination.limit,
                    total: pagination.total,
                    totalPages: pagination.total_pages,
                  }}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
